import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  Pagination
} from '@mui/material'
import {
  Add as AddIcon,
  Inventory2 as BoxIcon,
  LockOpen as OpenIcon,
  CheckCircle as FinishedIcon
} from '@mui/icons-material'
import {
  useGetBoxesQuery,
  useUpdateBoxStatusMutation,
  CigarBox,
  BoxSearchParams
} from '@store/api/boxApi'
import { useAppSelector } from '@store/hooks'
import BoxForm from '@components/boxes/BoxForm'
import PageHeader from '@components/layout/PageHeader'
import { trackFeature } from '@utils/trackFeature'

const statusColors: Record<string, 'default' | 'primary' | 'success' | 'warning'> = {
  sealed: 'primary',
  opened: 'warning',
  finished: 'success',
}

const statusIcons: Record<string, React.ReactNode> = {
  sealed: <BoxIcon fontSize="small" />,
  opened: <OpenIcon fontSize="small" />,
  finished: <FinishedIcon fontSize="small" />,
}

const Boxes: React.FC = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [showForm, setShowForm] = useState(false)

  const searchParams: BoxSearchParams = {
    page,
    limit: 12,
    ...(statusFilter && { status: statusFilter }),
  }

  const { data: boxesResponse, isLoading, error } = useGetBoxesQuery(searchParams, {
    skip: !isAuthenticated,
  })

  const [updateBoxStatus] = useUpdateBoxStatusMutation()

  const boxes = boxesResponse?.data || []
  const pagination = boxesResponse?.pagination

  const handleStatusChange = async (boxId: string, newStatus: 'sealed' | 'opened' | 'finished') => {
    try {
      await updateBoxStatus({ id: boxId, status: newStatus }).unwrap()
    } catch (err) {
      console.error('Failed to update box status:', err)
    }
  }

  const getNextStatus = (current: string): 'opened' | 'finished' | null => {
    if (current === 'sealed') return 'opened'
    if (current === 'opened') return 'finished'
    return null
  }

  const formatPrice = (price?: number | string) => {
    if (!price) return '—'
    const num = typeof price === 'string' ? parseFloat(price) : price
    return `$${num.toFixed(2)}`
  }

  if (isLoading) {
    return (
      <>
        <PageHeader title="Boxes" />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title="Boxes"
        primaryAction={{
          label: 'Add box',
          icon: <AddIcon />,
          onClick: () => setShowForm(true),
        }}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3} sx={{ width: '100%' }}>
        <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 150 }, width: { xs: '100%', sm: 'auto' } }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(1)
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="sealed">Sealed</MenuItem>
            <MenuItem value="opened">Opened</MenuItem>
            <MenuItem value="finished">Finished</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load boxes. Please try again.
        </Alert>
      )}

      {/* Empty State */}
      {!isLoading && boxes.length === 0 && (
        <Box textAlign="center" py={8}>
          <BoxIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No boxes yet
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Add your first box of cigars to start tracking your collection.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              trackFeature('empty_state_cta', { action: 'add_box' })
              setShowForm(true)
            }}
          >
            Add Your First Box
          </Button>
        </Box>
      )}

      {/* Box Grid */}
      <Grid container spacing={2}>
        {boxes.map((box: CigarBox) => {
          const nextStatus = getNextStatus(box.status)
          const costPerCigar = box.purchasePrice && box.cigarsPerBox
            ? (Number(box.purchasePrice) / box.cigarsPerBox).toFixed(2)
            : null

          return (
            <Grid item xs={12} sm={6} md={4} key={box.id}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="h6" noWrap sx={{ maxWidth: '70%' }}>
                      {box.cigarLine?.brand?.name || 'Unknown Brand'}
                    </Typography>
                    <Chip
                      icon={statusIcons[box.status] as React.ReactElement}
                      label={box.status.charAt(0).toUpperCase() + box.status.slice(1)}
                      color={statusColors[box.status]}
                      size="small"
                    />
                  </Stack>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {box.cigarLine?.name || 'Unknown Line'}
                  </Typography>

                  <Stack spacing={0.5} mt={2}>
                    <Typography variant="body2">
                      <strong>Size:</strong> {box.boxSize} ({box.cigarsPerBox} cigars)
                    </Typography>
                    <Typography variant="body2">
                      <strong>Price:</strong> {formatPrice(box.purchasePrice)}
                      {costPerCigar && ` (${formatPrice(costPerCigar)}/cigar)`}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Humidor:</strong> {box.humidor?.name || '—'}
                    </Typography>
                    {box.purchaseDate && (
                      <Typography variant="body2">
                        <strong>Purchased:</strong> {new Date(box.purchaseDate).toLocaleDateString()}
                      </Typography>
                    )}
                    {box.notes && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                        {box.notes}
                      </Typography>
                    )}
                  </Stack>
                </CardContent>

                {nextStatus && (
                  <CardActions sx={{ px: 2, pb: 2 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleStatusChange(box.id, nextStatus)}
                    >
                      Mark as {nextStatus.charAt(0).toUpperCase() + nextStatus.slice(1)}
                    </Button>
                  </CardActions>
                )}
              </Card>
            </Grid>
          )
        })}
      </Grid>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={pagination.pages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Add Box Form */}
      <BoxForm
        open={showForm}
        onClose={() => setShowForm(false)}
      />
    </>
  )
}

export default Boxes
